import { lazy, Suspense, useState } from "react";
import styles from "./reportsContent.module.scss";
import { bucketNameMapping, EMPTY_STRING, reportStageMapping } from "src/components/constant/constant";
import CircularLoader from "src/components/UI/circularLoader/CircularLoader";
import { stageColors } from "src/variable/randomColor";
import { Link } from "react-router-dom";
import { ReportsContent } from "./reportsContentSlice";
import { dateTimeFormat, getDecimalNumber } from "src/utils";
import { CopyToClipboard } from "react-copy-to-clipboard";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import AdminSwitchOrg from "src/components/adminSwitchOrg/AdminSwitchOrg";
import { Tooltip } from "@mui/material";

const DataModal = lazy(() => import("src/components/internalFileContent/dataModal/DataModal"));

const OrganisationColumn = ({ data, report }: { data: any; report: ReportsContent }) => {
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = () => {
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 300);
  };

  return (
    <div>
      <div className={styles.organisationHeading}>
        <Link to={`/account/${report?.object_id}`}>
          {data?.organisation_id ? data?.organisation_name : report?.object_id}
        </Link>
        <Tooltip title="Account ID Copied!" open={isCopied} placement="top" arrow>
          <span>
            <CopyToClipboard text={report?.object_id || EMPTY_STRING} onCopy={handleCopy}>
              <button
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  background: "none",
                  border: "none",
                  cursor: "pointer"
                }}
              >
                <ContentCopyOutlinedIcon fontSize="inherit" />
              </button>
            </CopyToClipboard>
          </span>
        </Tooltip>
      </div>
      <div>{data?.organisation_type?.toLowerCase()}</div>
    </div>
  );
};

const reportContentColumns = [
  {
    title: "Organisation",
    dataIndex: "data",
    key: "object_id",
    render: (data: any, report: ReportsContent) => <OrganisationColumn data={data} report={report} />
  },
  {
    title: "Stage",
    dataIndex: "data",
    key: "stage",
    render: (data: { stage: string }) => (
      <div className={styles.type}>
        <div
          className={styles.typeColor}
          style={{
            backgroundColor: `${reportStageMapping[data.stage] ? stageColors[data.stage] : EMPTY_STRING}`,
            color: `${reportStageMapping[data.stage] ? "#ffffff" : EMPTY_STRING}`,
            fontWeight: 600
          }}
        >
          {reportStageMapping[data?.stage] || data?.stage}
        </div>
      </div>
    )
  },
  {
    title: "Bucket Name",
    dataIndex: "data",
    key: "bucket",
    render: (data: { bucket: string }) => <div>{bucketNameMapping[data.bucket] ?? "---"}</div>
  },
  {
    title: "Last Transaction Detail",
    dataIndex: "data",
    key: "last_transaction_details",
    render: (data: { last_transaction_details: { date: string; amount: string } }) => (
      <div>
        {data?.last_transaction_details ? (
          <>
            <div>
              <strong>Date:</strong> {dateTimeFormat(data?.last_transaction_details?.date)?.datetime}
            </div>
            <div>
              <strong>Amount:</strong> ${getDecimalNumber(Number(data?.last_transaction_details?.amount))}
            </div>
          </>
        ) : (
          <div>---</div>
        )}
      </div>
    )
  },
  {
    title: "Last Deposit Detail",
    dataIndex: "data",
    key: "last_deposit_details",
    render: (data: { last_deposit_details: { date: string; amount: string } }) => (
      <div>
        {data?.last_deposit_details ? (
          <>
            <div>
              <strong>Date:</strong> {dateTimeFormat(data?.last_deposit_details?.date)?.datetime}
            </div>
            <div>
              <strong>Amount:</strong> ${getDecimalNumber(Number(data?.last_deposit_details?.amount))}
            </div>
          </>
        ) : (
          <div>---</div>
        )}
      </div>
    )
  },
  {
    title: "Today Transaction Detail",
    dataIndex: "data",
    key: "today_transaction_details",
    render: (data: { today_transactions_amount: string; today_transactions: string }) => (
      <div>
        <div>
          <strong>Number:</strong> {data?.today_transactions}
        </div>
        <div>
          <strong>Amount:</strong> ${getDecimalNumber(Number(data?.today_transactions_amount))}
        </div>
      </div>
    )
  },
  {
    title: "Today deposit details",
    dataIndex: "data",
    key: "today_deposit_details",
    render: (data: { today_deposit_amount: string; today_deposit: string }) => (
      <div>
        <div>
          <strong>Number:</strong> {data?.today_deposit}
        </div>
        <div>
          <strong>Amount:</strong> ${getDecimalNumber(Number(data?.today_deposit_amount))}
        </div>
      </div>
    )
  },
  {
    title: "Total Cards Balance",
    dataIndex: "data",
    key: "total_wallets_balance",
    render: (data: { total_wallets_balance: string }) => (
      <div>${getDecimalNumber(Number(data.total_wallets_balance))}</div>
    )
  },
  {
    title: "Total Cards",
    dataIndex: "data",
    key: "total_wallets",
    render: (data: { total_wallets: string }) => <div>{data.total_wallets}</div>
  },
  {
    title: "Total Cards",
    dataIndex: "data",
    key: "total_cards",
    render: (data: { total_cards: string }) => <div>{data.total_cards}</div>
  },
  {
    title: "Total Users",
    dataIndex: "data",
    key: "total_users",
    render: (data: { total_users: string }) => <div>{data.total_users}</div>
  },
  {
    title: "Total Licences",
    dataIndex: "data",
    key: "total_licences",
    render: (data: { total_licences: string }) => <div>{data.total_licences}</div>
  },
  {
    title: "Data",
    dataIndex: "data",
    key: "data",
    render: (data: object, report: ReportsContent) => {
      return (
        <Suspense fallback={<CircularLoader />}>
          <DataModal
            data={data}
            headerChildren={<AdminSwitchOrg orgId={report.object_id} buttonText="Switch to this Organisation" />}
          />
        </Suspense>
      );
    }
  }
];

const reportCardConfig = [
  {
    title: "Bucket",
    key: "bucket",
    dataIndex: "data",
    render: (data: { bucket: string }) => <div>{bucketNameMapping[data.bucket] ?? "---"}</div>
  },
  {
    title: "Last Transaction Detail",
    dataIndex: "data",
    key: "last_transaction_details",
    render: (data: { last_transaction_details: { date: string; amount: string } }) => (
      <div>
        {data?.last_transaction_details ? (
          <>
            <div>
              <strong>Date:</strong> {dateTimeFormat(data?.last_transaction_details?.date)?.datetime}
            </div>
            <div>
              <strong>Amount:</strong> ${getDecimalNumber(Number(data?.last_transaction_details?.amount))}
            </div>
          </>
        ) : (
          <div>---</div>
        )}
      </div>
    )
  },
  {
    title: "Last Deposit Detail",
    dataIndex: "data",
    key: "last_deposit_details",
    render: (data: { last_deposit_details: { date: string; amount: string } }) => (
      <div>
        {data?.last_deposit_details ? (
          <>
            <div>
              <strong>Date:</strong> {dateTimeFormat(data?.last_deposit_details?.date)?.datetime}
            </div>
            <div>
              <strong>Amount:</strong> ${getDecimalNumber(Number(data?.last_deposit_details?.amount))}
            </div>
          </>
        ) : (
          <div>---</div>
        )}
      </div>
    )
  },
  {
    title: "Total Users",
    dataIndex: "data",
    key: "total_users",
    render: (data: { total_users: string }) => <div>{data.total_users}</div>
  }
];

const convertToCSV = (data: any[]) => {
  const headers = [
    "Organisation Id",
    "Organisation",
    "Organisation Type",
    "Stage",
    "Bucket Name",
    "Organisation Contact - Name",
    "Organisation Contact - Email",
    "Organisation Contact - Phone",
    "Last Transaction Detail (Date)",
    "Last Transaction Detail (Amount)",
    "Last Deposit Detail (Date)",
    "Last Deposit Detail (Amount)",
    "Today Transaction Detail (Number)",
    "Today Transaction Detail (Amount)",
    "Today Deposit Detail (Number)",
    "Today Deposit Detail (Amount)",
    "Total Wallets Balance",
    "Total Wallets",
    "Total Cards",
    "Total Licences",
    "Card Ordered (This Month)",
    "Total Users"
  ];

  const rows = data.map((item) => {
    const dataObj = item.data;

    return [
      item.object_id,
      dataObj.organisation_name || "---",
      dataObj?.organisation_type || "---",
      reportStageMapping[dataObj.stage] || dataObj.stage,
      bucketNameMapping[dataObj.bucket] || "---",
      dataObj.organisation_contact?.name || "---",
      dataObj.organisation_contact?.phone || "---",
      dataObj.organisation_contact?.email || "---",
      dataObj.last_transaction_details?.date ? dateTimeFormat(dataObj.last_transaction_details.date)?.datetime : "---",
      dataObj.last_transaction_details?.amount
        ? `$${getDecimalNumber(Number(dataObj.last_transaction_details.amount))}`
        : "---",
      dataObj.last_deposit_details?.date ? dateTimeFormat(dataObj.last_deposit_details.date)?.datetime : "---",
      dataObj.last_deposit_details?.amount
        ? `$${getDecimalNumber(Number(dataObj.last_deposit_details.amount))}`
        : "---",
      dataObj.today_transactions || "0",
      dataObj.today_transactions_amount ? `$${getDecimalNumber(Number(dataObj.today_transactions_amount))}` : "$0",
      dataObj.today_deposit || "0",
      dataObj.today_deposit_amount ? `$${getDecimalNumber(Number(dataObj.today_deposit_amount))}` : "$0",
      dataObj.total_wallets_balance ? `$${getDecimalNumber(Number(dataObj.total_wallets_balance))}` : "$0",
      dataObj.total_wallets || "0",
      dataObj.total_cards || "0",
      dataObj.total_licences || "0",
      dataObj.this_month_cards_count || "0",
      dataObj.total_users || "0"
    ];
  });

  const csvContent = [
    headers.join(","),
    ...rows.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, "EMPTY_STRING")}"`).join(","))
  ].join("\n");

  return csvContent;
};

export { reportContentColumns, reportCardConfig, convertToCSV };
